import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { useAuth } from '@/hooks/useAuth';
import { usePrefersAdminExperience } from '@/hooks/usePrefersAdminExperience';
import { getHomeRoute } from '@/utils/roleRouting';
import { createLoginSchema, type LoginFormData } from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import styles from './LoginPage.module.scss';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const prefersAdmin = usePrefersAdminExperience();
  const navigate = useNavigate();
  const location = useLocation();

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Si venía redirigido de una página protegida, respetamos ese destino; si no,
  // la "home" según rol + dispositivo (regla del dual: escritorio → admin).
  const from = (location.state as { from?: string })?.from;

  const onSubmit = async (data: LoginFormData) => {
    try {
      const user = await login(data);
      navigate(from ?? getHomeRoute(user, prefersAdmin), { replace: true });
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : t('auth.errors.loginFailed'),
      });
    }
  };

  return (
    <AuthLayout title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      <form className={styles.loginForm} onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className={styles.loginForm__error} role="alert">
            {errors.root.message}
          </div>
        )}

        <FormField
          label={t('auth.login.emailLabel')}
          type="email"
          placeholder={t('auth.login.emailPlaceholder')}
          error={errors.email?.message}
          required
          autoComplete="email"
          {...register('email')}
        />

        <FormField
          label={t('auth.login.passwordLabel')}
          type="password"
          placeholder={t('auth.login.passwordPlaceholder')}
          error={errors.password?.message}
          required
          autoComplete="current-password"
          {...register('password')}
        />

        <Link to={ROUTES.AUTH.FORGOT_PASSWORD} className={styles.loginForm__forgotLink}>
          {t('auth.login.forgotPassword')}
        </Link>

        <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting}>
          {t('auth.login.submit')}
        </Button>

        {/*
          Invite-only por ahora: el alta es solo por invitación de empresa, así
          que ocultamos el enlace de registro público. Reactivar (junto con las
          claves auth.login.noAccount / signUp) cuando se active el B2C.
        */}
      </form>
    </AuthLayout>
  );
}

export default LoginPage;
